import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import DesignSystemNavigation from '@/components/DesignSystemNavigation';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableCaption,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { InfoIcon, AlertTriangleIcon, ArrowUpIcon } from 'lucide-react';

const DesignSystem: React.FC = () => {
  const [activeSection, setActiveSection] = useState('colors');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Hide chatbot widget on this page
  useEffect(() => {
    const beaconContainer = document.getElementById('beacon-container');
    if (beaconContainer) {
      beaconContainer.style.display = 'none';
    }
    return () => {
      if (beaconContainer) {
        beaconContainer.style.display = '';
      }
    };
  }, []);

  // Show/hide back to top button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setActiveSection(sectionId);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="pl-4 pr-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          {/* Navigation Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <DesignSystemNavigation
                activeSection={activeSection}
                onSectionClick={scrollToSection}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-5">
            <div className="space-y-16">
              {/* Colors Section */}
              <section
                ref={el => (sectionRefs.current['colors'] = el)}
                id="colors"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">Colors</h1>

                {/* Primary Colors */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Primary Colors
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-primary"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Primary
                          </h3>
                          <p className="text-sm text-gray-500">
                            oklch(0.3243 0.1937 281.7909)
                          </p>
                          <p className="text-sm text-gray-600">
                            Main brand color for primary actions
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-primary-foreground border border-gray-300"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Primary Foreground
                          </h3>
                          <p className="text-sm text-gray-500">
                            oklch(0.985 0 0)
                          </p>
                          <p className="text-sm text-gray-600">
                            Text color on primary backgrounds
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Semantic Colors */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Semantic Colors
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-destructive"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Destructive
                          </h3>
                          <p className="text-sm text-gray-500">
                            oklch(0.577 0.245 27.325)
                          </p>
                          <p className="text-sm text-gray-600">
                            Error states and destructive actions
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-destructive-foreground border border-gray-300"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Destructive Foreground
                          </h3>
                          <p className="text-sm text-gray-500">oklch(1 0 0)</p>
                          <p className="text-sm text-gray-600">
                            Text color on destructive backgrounds
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-secondary"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Secondary
                          </h3>
                          <p className="text-sm text-gray-500">
                            oklch(0.9702 0 0)
                          </p>
                          <p className="text-sm text-gray-600">
                            Secondary actions and subtle backgrounds
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-muted border border-gray-300"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Muted</h3>
                          <p className="text-sm text-gray-500">
                            oklch(0.97 0 0)
                          </p>
                          <p className="text-sm text-gray-600">
                            Muted backgrounds and subtle elements
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Background Colors */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Background Colors
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-background border border-gray-300"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Background
                          </h3>
                          <p className="text-sm text-gray-500">oklch(1 0 0)</p>
                          <p className="text-sm text-gray-600">
                            Main background color
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-card border border-gray-300"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Card</h3>
                          <p className="text-sm text-gray-500">oklch(1 0 0)</p>
                          <p className="text-sm text-gray-600">
                            Card and surface backgrounds
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-border"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Border
                          </h3>
                          <p className="text-sm text-gray-500">
                            oklch(0.922 0 0)
                          </p>
                          <p className="text-sm text-gray-600">
                            Border and divider colors
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg bg-input"></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">Input</h3>
                          <p className="text-sm text-gray-500">
                            oklch(0.922 0 0)
                          </p>
                          <p className="text-sm text-gray-600">
                            Input field backgrounds
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Chart Colors */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Chart Colors
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className="w-12 h-12 rounded-lg"
                          style={{ backgroundColor: '#f04438' }}
                        ></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Response Required
                          </h3>
                          <p className="text-sm text-gray-500">#f04438</p>
                          <p className="text-sm text-gray-600">
                            Red - Response required status
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className="w-12 h-12 rounded-lg"
                          style={{ backgroundColor: '#717680' }}
                        ></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Drafts
                          </h3>
                          <p className="text-sm text-gray-500">#717680</p>
                          <p className="text-sm text-gray-600">
                            Gray - Draft status
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className="w-12 h-12 rounded-lg"
                          style={{ backgroundColor: '#93c5fd' }}
                        ></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Pre-review
                          </h3>
                          <p className="text-sm text-gray-500">#93c5fd</p>
                          <p className="text-sm text-gray-600">
                            Light Blue - Pre-review status
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className="w-12 h-12 rounded-lg"
                          style={{ backgroundColor: '#2563eb' }}
                        ></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Review
                          </h3>
                          <p className="text-sm text-gray-500">#2563eb</p>
                          <p className="text-sm text-gray-600">
                            Blue - Review status
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className="w-12 h-12 rounded-lg"
                          style={{ backgroundColor: '#7c3aed' }}
                        ></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Post-review
                          </h3>
                          <p className="text-sm text-gray-500">#7c3aed</p>
                          <p className="text-sm text-gray-600">
                            Purple - Post-review status
                          </p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex items-center space-x-4">
                        <div
                          className="w-12 h-12 rounded-lg"
                          style={{ backgroundColor: '#f4af2d' }}
                        ></div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Expiring Soon
                          </h3>
                          <p className="text-sm text-gray-500">#f4af2d</p>
                          <p className="text-sm text-gray-600">
                            Yellow/Gold - Expiring soon status
                          </p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </section>

              <Separator />

              {/* Typography Section */}
              <section
                ref={el => (sectionRefs.current['typography'] = el)}
                id="typography"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">
                  Typography
                </h1>

                {/* Font Families */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Font Families
                  </h2>
                  <Card className="p-4">
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">
                        Primary Font (Sans-serif)
                      </p>
                      <p className="text-lg font-semibold text-gray-900">
                        Inter, ui-sans-serif, sans-serif, system-ui
                      </p>
                    </div>
                  </Card>
                </div>

                {/* Heading Scale */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Heading Scale
                  </h2>
                  <div className="space-y-4">
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Heading 1</p>
                        <h1 className="text-4xl font-semibold text-gray-900">
                          The quick brown fox jumps over the lazy dog
                        </h1>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Heading 2</p>
                        <h2 className="text-3xl font-semibold text-gray-900">
                          The quick brown fox jumps over the lazy dog
                        </h2>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Heading 3</p>
                        <h3 className="text-2xl font-medium text-gray-900">
                          The quick brown fox jumps over the lazy dog
                        </h3>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Heading 4</p>
                        <h4 className="text-xl font-medium text-gray-900">
                          The quick brown fox jumps over the lazy dog
                        </h4>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Heading 5</p>
                        <h5 className="text-lg font-medium text-gray-900">
                          The quick brown fox jumps over the lazy dog
                        </h5>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Heading 6</p>
                        <h6 className="text-base font-medium text-gray-900">
                          The quick brown fox jumps over the lazy dog
                        </h6>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Text Styles */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Text Styles
                  </h2>
                  <div className="space-y-4">
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Muted Text</p>
                        <p className="text-gray-500">
                          This is muted text for secondary information and
                          descriptions.
                        </p>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Bold Text</p>
                        <p className="font-semibold text-gray-900">
                          This is bold text for emphasis and important content.
                        </p>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Italic Text</p>
                        <p className="italic text-gray-900">
                          This is italic text for emphasis and quotes.
                        </p>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-gray-500">Underlined Text</p>
                        <p className="underline text-gray-900">
                          This is underlined text for links and emphasis.
                        </p>
                      </div>
                    </Card>
                  </div>
                </div>
              </section>

              <Separator />

              {/* Spacing & Layout Section */}
              <section
                ref={el => (sectionRefs.current['spacing-layout'] = el)}
                id="spacing-layout"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">
                  Spacing & Layout
                </h1>

                {/* Spacing Scale */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Spacing Scale
                  </h2>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">4px</span>
                      <div className="w-1 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">1</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">8px</span>
                      <div className="w-2 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">2</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">12px</span>
                      <div className="w-3 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">3</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">16px</span>
                      <div className="w-4 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">4</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">20px</span>
                      <div className="w-5 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">5</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">24px</span>
                      <div className="w-6 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">6</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">32px</span>
                      <div className="w-8 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">8</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">40px</span>
                      <div className="w-10 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">10</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">48px</span>
                      <div className="w-12 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">12</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">64px</span>
                      <div className="w-16 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">16</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">80px</span>
                      <div className="w-20 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">20</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">96px</span>
                      <div className="w-24 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">24</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">128px</span>
                      <div className="w-32 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">32</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">160px</span>
                      <div className="w-40 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">40</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">192px</span>
                      <div className="w-48 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">48</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">224px</span>
                      <div className="w-56 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">56</span>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500 w-12">256px</span>
                      <div className="w-64 h-4 bg-primary rounded"></div>
                      <span className="text-sm text-gray-400">64</span>
                    </div>
                  </div>
                </div>

                {/* Border Radius */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Border Radius
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="p-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-primary rounded-sm"></div>
                        <div className="text-center">
                          <p className="text-sm font-medium">rounded-sm</p>
                          <p className="text-xs text-gray-500">2px</p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-primary rounded-md"></div>
                        <div className="text-center">
                          <p className="text-sm font-medium">rounded-md</p>
                          <p className="text-xs text-gray-500">6px</p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-primary rounded-lg"></div>
                        <div className="text-center">
                          <p className="text-sm font-medium">rounded-lg</p>
                          <p className="text-xs text-gray-500">8px</p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-primary rounded-xl"></div>
                        <div className="text-center">
                          <p className="text-sm font-medium">rounded-xl</p>
                          <p className="text-xs text-gray-500">12px</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>

                {/* Shadows */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Shadows
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="p-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-white shadow-sm border border-gray-200 rounded"></div>
                        <div className="text-center">
                          <p className="text-sm font-medium">shadow-sm</p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-white shadow border border-gray-200 rounded"></div>
                        <div className="text-center">
                          <p className="text-sm font-medium">shadow</p>
                        </div>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="w-12 h-12 bg-white shadow-md border border-gray-200 rounded"></div>
                        <div className="text-center">
                          <p className="text-sm font-medium">shadow-md</p>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              </section>

              <Separator />

              {/* Buttons Section */}
              <section
                ref={el => (sectionRefs.current['buttons'] = el)}
                id="buttons"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">
                  Buttons
                </h1>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Button Variants
                  </h2>
                  <div className="flex flex-wrap gap-4">
                    <Button>Default</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="outline">Outline</Button>
                    <Button variant="ghost">Ghost</Button>
                    <Button variant="link">Link</Button>
                    <Button variant="destructive">Destructive</Button>
                  </div>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Button Sizes
                  </h2>
                  <div className="flex flex-wrap items-center gap-4">
                    <Button size="sm">Small</Button>
                    <Button size="default">Default</Button>
                    <Button size="lg">Large</Button>
                    <Button size="sm" className="w-10 h-10 p-0">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </Button>
                  </div>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Button States
                  </h2>
                  <div className="flex flex-wrap items-center gap-4">
                    <Button>Normal</Button>
                    <Button disabled>Disabled</Button>
                    <Button disabled className="opacity-60">
                      Loading
                    </Button>
                  </div>
                </div>
              </section>

              <Separator />

              {/* Form Elements Section */}
              <section
                ref={el => (sectionRefs.current['form-elements'] = el)}
                id="form-elements"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">
                  Form Elements
                </h1>

                {/* Input Fields */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Input Fields
                  </h2>
                  <div className="max-w-md space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="input-text">Text Input</Label>
                      <Input
                        id="input-text"
                        type="text"
                        placeholder="Enter text here"
                      />
                      <p className="text-sm text-gray-500">
                        Standard text input field
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="input-email">Email Input</Label>
                      <Input
                        id="input-email"
                        type="email"
                        placeholder="Enter email address"
                      />
                      <p className="text-sm text-gray-500">
                        Email input with validation
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="input-password">Password Input</Label>
                      <Input
                        id="input-password"
                        type="password"
                        placeholder="Enter password"
                      />
                      <p className="text-sm text-gray-500">
                        Password input with hidden text
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="input-disabled">Disabled Input</Label>
                      <Input
                        id="input-disabled"
                        type="text"
                        placeholder="This field is disabled"
                        disabled
                      />
                      <p className="text-sm text-gray-500">
                        Disabled input field
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="input-error">Input with Error</Label>
                      <Input
                        id="input-error"
                        type="text"
                        placeholder="This field has an error"
                        className="border-destructive focus-visible:ring-destructive"
                      />
                      <p className="text-sm text-destructive">
                        This field has an error
                      </p>
                    </div>
                  </div>
                </div>

                {/* Textarea */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Textarea
                  </h2>
                  <div className="max-w-md space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="textarea-example">Message</Label>
                      <Textarea
                        id="textarea-example"
                        placeholder="Enter your message here..."
                        rows={4}
                      />
                      <p className="text-sm text-gray-500">
                        Multi-line text input
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="textarea-disabled">
                        Disabled Textarea
                      </Label>
                      <Textarea
                        id="textarea-disabled"
                        placeholder="This textarea is disabled"
                        rows={3}
                        disabled
                      />
                      <p className="text-sm text-gray-500">
                        Disabled textarea field
                      </p>
                    </div>
                  </div>
                </div>

                {/* Select Dropdown */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Select Dropdown
                  </h2>
                  <div className="max-w-md space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="select-example">Choose an option</Label>
                      <Select>
                        <SelectTrigger>
                          <SelectValue placeholder="Select an option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="option1">Option 1</SelectItem>
                          <SelectItem value="option2">Option 2</SelectItem>
                          <SelectItem value="option3">Option 3</SelectItem>
                          <SelectItem value="option4">Option 4</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-gray-500">
                        Dropdown selection
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="select-disabled">Disabled Select</Label>
                      <Select disabled>
                        <SelectTrigger>
                          <SelectValue placeholder="This select is disabled" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="option1">Option 1</SelectItem>
                          <SelectItem value="option2">Option 2</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-gray-500">Disabled dropdown</p>
                    </div>
                  </div>
                </div>

                {/* Checkboxes */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Checkboxes
                  </h2>
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Checkbox id="checkbox-unchecked" />
                        <Label htmlFor="checkbox-unchecked">Unchecked</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="checkbox-checked" defaultChecked />
                        <Label htmlFor="checkbox-checked">Checked</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="checkbox-disabled" disabled />
                        <Label htmlFor="checkbox-disabled">Disabled</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="checkbox-disabled-checked"
                          defaultChecked
                          disabled
                        />
                        <Label htmlFor="checkbox-disabled-checked">
                          Disabled & Checked
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Radio Groups */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Radio Groups
                  </h2>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Choose one option</Label>
                      <RadioGroup defaultValue="option1">
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="option1" id="radio1" />
                          <Label htmlFor="radio1">Option 1</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="option2" id="radio2" />
                          <Label htmlFor="radio2">Option 2</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="option3" id="radio3" />
                          <Label htmlFor="radio3">Option 3</Label>
                        </div>
                      </RadioGroup>
                      <p className="text-sm text-gray-500">
                        Single selection from multiple options
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Disabled Radio Group</Label>
                      <RadioGroup defaultValue="option1" disabled>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="option1"
                            id="radio-disabled1"
                          />
                          <Label htmlFor="radio-disabled1">Option 1</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="option2"
                            id="radio-disabled2"
                          />
                          <Label htmlFor="radio-disabled2">Option 2</Label>
                        </div>
                      </RadioGroup>
                      <p className="text-sm text-gray-500">
                        Disabled radio group
                      </p>
                    </div>
                  </div>
                </div>

                {/* Switch/Toggle */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Switch/Toggle
                  </h2>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Switch id="switch-off" />
                      <Label htmlFor="switch-off">Switch Off</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="switch-on" defaultChecked />
                      <Label htmlFor="switch-on">Switch On</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch id="switch-disabled" disabled />
                      <Label htmlFor="switch-disabled">Disabled Switch</Label>
                    </div>
                    <p className="text-sm text-gray-500">
                      Toggle switch for on/off states
                    </p>
                  </div>
                </div>

                {/* Form Layout Example */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Form Layout Example
                  </h2>
                  <Card className="max-w-md">
                    <CardHeader>
                      <CardTitle>Contact Form</CardTitle>
                      <CardDescription>
                        Example of how form elements work together
                      </CardDescription>
                    </CardHeader>
                    <CardFooter className="space-y-4">
                      <div className="space-y-4 w-full">
                        <div className="space-y-2">
                          <Label htmlFor="form-name">Full Name</Label>
                          <Input
                            id="form-name"
                            placeholder="Enter your full name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="form-email">Email</Label>
                          <Input
                            id="form-email"
                            type="email"
                            placeholder="Enter your email"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="form-subject">Subject</Label>
                          <Select>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a subject" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">
                                General Inquiry
                              </SelectItem>
                              <SelectItem value="support">Support</SelectItem>
                              <SelectItem value="feedback">Feedback</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="form-message">Message</Label>
                          <Textarea
                            id="form-message"
                            placeholder="Enter your message"
                            rows={4}
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox id="form-newsletter" />
                          <Label htmlFor="form-newsletter">
                            Subscribe to newsletter
                          </Label>
                        </div>
                        <Button className="w-full">Send Message</Button>
                      </div>
                    </CardFooter>
                  </Card>
                </div>
              </section>

              <Separator />

              {/* Cards Section */}
              <section
                ref={el => (sectionRefs.current['cards'] = el)}
                id="cards"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">Cards</h1>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Basic Card
                  </h2>
                  <Card className="max-w-md">
                    <CardHeader>
                      <CardTitle>Card Title</CardTitle>
                      <CardDescription>
                        This is a basic card with title and body text content.
                      </CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              </section>

              <Separator />

              {/* Tables Section */}
              <section
                ref={el => (sectionRefs.current['tables'] = el)}
                id="tables"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">Tables</h1>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Basic Table
                  </h2>
                  <Table>
                    <TableCaption>A list of your recent data.</TableCaption>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Column 1</TableHead>
                        <TableHead>Column 2</TableHead>
                        <TableHead>Column 3</TableHead>
                        <TableHead>Column 4</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell>Data 1</TableCell>
                        <TableCell>Data 2</TableCell>
                        <TableCell>Data 3</TableCell>
                        <TableCell>Data 4</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Data 5</TableCell>
                        <TableCell>Data 6</TableCell>
                        <TableCell>Data 7</TableCell>
                        <TableCell>Data 8</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </section>

              <Separator />

              {/* Feedback Section */}
              <section
                ref={el => (sectionRefs.current['feedback'] = el)}
                id="feedback"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">
                  Feedback
                </h1>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Alerts
                  </h2>
                  <div className="space-y-4">
                    <Alert>
                      <AlertTitle>Success!</AlertTitle>
                      <AlertDescription>
                        Your action was completed successfully.
                      </AlertDescription>
                    </Alert>
                    <Alert>
                      <AlertTitle>Info</AlertTitle>
                      <AlertDescription>
                        Here&apos;s some helpful information for you.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Badges
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <Badge>Default</Badge>
                    <Badge variant="secondary">Secondary</Badge>
                    <Badge variant="destructive">Destructive</Badge>
                    <Badge variant="outline">Outline</Badge>
                  </div>
                </div>
              </section>

              <Separator />

              {/* Navigation Section */}
              <section
                ref={el => (sectionRefs.current['navigation'] = el)}
                id="navigation"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">
                  Navigation
                </h1>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">Tabs</h2>
                  <Tabs defaultValue="tab1" className="w-full">
                    <TabsList>
                      <TabsTrigger value="tab1">Active Tab</TabsTrigger>
                      <TabsTrigger value="tab2">Inactive Tab</TabsTrigger>
                      <TabsTrigger value="tab3">Another Tab</TabsTrigger>
                    </TabsList>
                    <TabsContent value="tab1" className="mt-4">
                      <Card>
                        <CardHeader>
                          <CardTitle>Tab 1 Content</CardTitle>
                          <CardDescription>
                            This is the content for the first tab.
                          </CardDescription>
                        </CardHeader>
                      </Card>
                    </TabsContent>
                  </Tabs>
                </div>
              </section>

              <Separator />

              {/* Progress Section */}
              <section
                ref={el => (sectionRefs.current['progress'] = el)}
                id="progress"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">
                  Progress
                </h1>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Progress Bars
                  </h2>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">25% Complete</p>
                      <Progress value={25} className="w-full" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">50% Complete</p>
                      <Progress value={50} className="w-full" />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-500">75% Complete</p>
                      <Progress value={75} className="w-full" />
                    </div>
                  </div>
                </div>
              </section>

              <Separator />

              {/* Badges Section */}
              <section
                ref={el => (sectionRefs.current['badges'] = el)}
                id="badges"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">Badges</h1>

                {/* Status Badges */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Status Badges
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-red-100 text-red-800 border-red-200">
                      Response Required
                    </Badge>
                    <Badge variant="outline">Draft</Badge>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      Pre-review
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      Review
                    </Badge>
                    <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                      Post-review
                    </Badge>
                    <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                      Expiring Soon
                    </Badge>
                  </div>
                </div>

                {/* Determination Badges */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Determination Badges
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      Approved
                    </Badge>
                    <Badge className="bg-red-100 text-red-800 border-red-200">
                      Non-compliance
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      Acknowledged
                    </Badge>
                    <Badge className="bg-orange-100 text-orange-800 border-orange-200">
                      Pending
                    </Badge>
                  </div>
                </div>

                {/* Activity Type Badges */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Activity Type Badges
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline">Request</Badge>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      Review
                    </Badge>
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      New submission
                    </Badge>
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      Approval
                    </Badge>
                    <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
                      Report
                    </Badge>
                    <Badge className="bg-red-100 text-red-800 border-red-200">
                      Adverse Event
                    </Badge>
                    <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                      Site Visit
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                      Deviation
                    </Badge>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                      Training
                    </Badge>
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      Final Report
                    </Badge>
                  </div>
                </div>

                {/* Standard Badge Variants */}
                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Standard Badge Variants
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <Badge>DEFAULT</Badge>
                    <Badge variant="secondary">SECONDARY</Badge>
                    <Badge variant="outline">OUTLINE</Badge>
                    <Badge variant="destructive">DESTRUCTIVE</Badge>
                  </div>
                </div>
              </section>

              <Separator />

              {/* Alerts Section */}
              <section
                ref={el => (sectionRefs.current['alerts'] = el)}
                id="alerts"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">Alerts</h1>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Alert Types
                  </h2>
                  <div className="space-y-4">
                    <Alert>
                      <InfoIcon className="h-4 w-4" />
                      <AlertTitle>Information</AlertTitle>
                      <AlertDescription>
                        This is an informational alert message.
                      </AlertDescription>
                    </Alert>
                    <Alert variant="destructive">
                      <AlertTriangleIcon className="h-4 w-4" />
                      <AlertTitle>Error</AlertTitle>
                      <AlertDescription>
                        This is an error alert message.
                      </AlertDescription>
                    </Alert>
                  </div>
                </div>
              </section>

              <Separator />

              {/* Overlays Section */}
              <section
                ref={el => (sectionRefs.current['overlays'] = el)}
                id="overlays"
                className="space-y-8"
              >
                <h1 className="text-3xl font-semibold text-gray-900">
                  Overlays
                </h1>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Dropdown Menu
                  </h2>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">Open Menu</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuLabel>My Account</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>Profile</DropdownMenuItem>
                      <DropdownMenuItem>Billing</DropdownMenuItem>
                      <DropdownMenuItem>Settings</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem>Logout</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Dialog
                  </h2>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">Open Dialog</Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Edit profile</DialogTitle>
                        <DialogDescription>
                          Make changes to your profile here. Click save when
                          you&apos;re done.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="name" className="text-right">
                            Name
                          </Label>
                          <Input
                            id="name"
                            defaultValue="Pedro Duarte"
                            className="col-span-3"
                          />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                          <Label htmlFor="username" className="text-right">
                            Username
                          </Label>
                          <Input
                            id="username"
                            defaultValue="@peduarte"
                            className="col-span-3"
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="submit">Save changes</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">Sheet</h2>
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline">Open Sheet</Button>
                    </SheetTrigger>
                    <SheetContent>
                      <SheetHeader>
                        <SheetTitle>Edit profile</SheetTitle>
                        <SheetDescription>
                          Make changes to your profile here. Click save when
                          you&apos;re done.
                        </SheetDescription>
                      </SheetHeader>
                      <div className="grid gap-4 py-4">
                        <div className="grid gap-3">
                          <Label htmlFor="sheet-name">Name</Label>
                          <Input id="sheet-name" defaultValue="Pedro Duarte" />
                        </div>
                        <div className="grid gap-3">
                          <Label htmlFor="sheet-username">Username</Label>
                          <Input id="sheet-username" defaultValue="@peduarte" />
                        </div>
                      </div>
                      <SheetFooter>
                        <Button type="submit">Save changes</Button>
                      </SheetFooter>
                    </SheetContent>
                  </Sheet>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Popover
                  </h2>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline">Open Popover</Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80">
                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <h4 className="font-medium leading-none">
                            Dimensions
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Set the dimensions for the layer.
                          </p>
                        </div>
                        <div className="grid gap-2">
                          <div className="grid grid-cols-3 items-center gap-4">
                            <Label htmlFor="width">Width</Label>
                            <Input
                              id="width"
                              defaultValue="100%"
                              className="col-span-2 h-8"
                            />
                          </div>
                          <div className="grid grid-cols-3 items-center gap-4">
                            <Label htmlFor="maxWidth">Max. width</Label>
                            <Input
                              id="maxWidth"
                              defaultValue="300px"
                              className="col-span-2 h-8"
                            />
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Tooltip
                  </h2>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="outline">Hover me</Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Add to library</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="space-y-6">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Avatars
                  </h2>
                  <div className="flex items-center gap-4">
                    <Avatar>
                      <AvatarImage
                        src="https://github.com/shadcn.png"
                        alt="@shadcn"
                      />
                      <AvatarFallback>CN</AvatarFallback>
                    </Avatar>
                    <Avatar>
                      <AvatarFallback>JD</AvatarFallback>
                    </Avatar>
                    <Avatar className="h-12 w-12">
                      <AvatarImage
                        src="https://github.com/vercel.png"
                        alt="@vercel"
                      />
                      <AvatarFallback>VC</AvatarFallback>
                    </Avatar>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          size="icon"
          className="fixed bottom-8 right-8 shadow-lg z-50"
          aria-label="Back to top"
        >
          <ArrowUpIcon className="h-5 w-5" />
        </Button>
      )}
    </div>
  );
};

export default DesignSystem;
